import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { PasswordChange } from '../model/auth/password-change.model';
import { Credentials } from '../model/credentials.model';
import { HttpParams } from '../model/http/http-params';
import { Profile } from '../model/profile.model';
import { User } from '../model/user.model';
import { SessionService } from './session.service';

@Injectable({providedIn: 'root'})
export class AuthService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private session = inject(SessionService);

    changePassword(passwordChange: PasswordChange): Observable<void> {
        return this.http.post<void>(`/api/change-password?token=${passwordChange.token}`, {
            plainPassword: {
                password: passwordChange.password,
                confirmation: passwordChange.confirmation,
            },
        }).pipe(take(1));
    }

    checkAuth(): Observable<void> {
        return this.http.head<void>('/api/ping').pipe(take(1));
    }

    initRedirection(): void {
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                take(1),
            )
            .subscribe(() => this.redirectToApp());
    }

    login(user: Credentials): Observable<User> {
        return this.http.post<User>('/api/login_check', {
            username: user.username,
            password: user.password,
        })
            .pipe(tap((response: User) => this.session.storeUser({...response.profile})));
    }

    redirectToApp(): void {
        if (this.session.isValid()) {
            const user = this.session.user();
            const redirectTo = localStorage.getItem('redirectTo');
            localStorage.removeItem('redirectTo');

            if (user?.isBackend) {
                return this.redirectToBackend(redirectTo);
            }

            if (user?.isFrontend) {
                return this.redirectToPortal(redirectTo);
            }
        }
    }

    resetPassword(username: string): Observable<void> {
        return this.http.post<void>('/api/reset-password', {username}).pipe(take(1));
    }

    resetUserSwitched(): void {
        this.session.resetUserSwitched();
        this.redirectToApp();
    }

    switchUser(establishment: number, username: string|null = null): void {
        const query = username ? `?${new HttpParams({_switch_user: username}).toString()}` : '';

        this.http.post<Profile>(`/api/portal/account/user/switch${query}`, {establishment})
            .pipe(take(1))
            .pipe(tap((profile: Profile) => this.session.switchUser(profile)))
            .subscribe(() => this.redirectToApp());
    }

    private redirectToBackend(redirectTo: string|null) {
        if (this.router.url.startsWith('/backend')) {
            return;
        }

        if (
            redirectTo
            && !redirectTo.startsWith('/backend')
            && !redirectTo.startsWith('/admin')
            && !redirectTo.startsWith('/doc')
        ) {
            redirectTo = null;
        }

        this.router.navigateByUrl(redirectTo || '/backend');
    }

    private redirectToPortal(redirectTo: string|null) {
        if (this.router.url.startsWith('/portail')) {
            return;
        }

        if (redirectTo && !redirectTo.startsWith('/portail')) {
            redirectTo = null;
        }
        this.router.navigateByUrl(redirectTo || '/portail');
    }
}
